import { forwardRef } from 'react';

const formTypeConfig = {
  '1099-NEC': {
    title: 'Form 1099-NEC',
    subtitle: 'Nonemployee Compensation',
    omb: '1545-0115',
    box1Label: '1 Nonemployee compensation',
    box2Label: '2 Payer made direct sales totaling $5,000 or more',
    instructions: 'Box 1. Shows nonemployee compensation. If you are in the trade or business of being a recipient, you are generally required to report this amount as self-employment income on Schedule SE (Form 1040).',
  },
  '1099-MISC': {
    title: 'Form 1099-MISC',
    subtitle: 'Miscellaneous Information',
    omb: '1545-0115',
    box1Label: '1 Rents',
    box2Label: '2 Royalties',
    instructions: 'Box 1. Shows rents paid to you. Box 2. Shows royalties paid to you. Report these amounts on Schedule E (Form 1040).',
  },
  '1099-DIV': {
    title: 'Form 1099-DIV',
    subtitle: 'Dividends and Distributions',
    omb: '1545-0110',
    box1Label: '1a Total ordinary dividends',
    box2Label: '1b Qualified dividends',
    instructions: 'Box 1a. Shows total ordinary dividends that are taxable. Include this amount on the "Ordinary dividends" line of Form 1040 or 1040-SR.',
  },
};

export const Form1099PrintView = forwardRef(({ payee, llc, year, totalAmount, formType = '1099-NEC' }, ref) => {
  const config = formTypeConfig[formType] || formTypeConfig['1099-NEC'];
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatAddress = (entity) => {
    if (!entity) return '';
    const parts = [];
    if (entity.address_line1) parts.push(entity.address_line1);
    if (entity.address_line2) parts.push(entity.address_line2);
    const cityStateZip = [
      entity.city,
      entity.state,
      entity.zip_code
    ].filter(Boolean).join(', ');
    if (cityStateZip) parts.push(cityStateZip);
    return parts;
  };

  const payerAddress = llc ? formatAddress({
    address_line1: llc.address_line1,
    address_line2: llc.address_line2,
    city: llc.city,
    state: llc.state,
    zip_code: llc.zip_code
  }) : [];

  return (
    <div ref={ref} className="bg-white p-8" style={{ width: '8.5in', minHeight: '11in' }}>
      {/* Form Header */}
      <div className="border-2 border-black">
        {/* Top Section */}
        <div className="flex border-b-2 border-black">
          {/* Left Column - Payer Info */}
          <div className="w-1/2 border-r-2 border-black p-3">
            <div className="text-[10px] text-red-600 font-bold mb-1">PAYER'S name, street address, city or town, state or province, country, ZIP or foreign postal code, and telephone no.</div>
            <div className="text-sm font-semibold">{llc?.company_name || 'Company Name'}</div>
            {payerAddress.map((line, i) => (
              <div key={i} className="text-sm">{line}</div>
            ))}
            {llc?.ein && <div className="text-sm mt-1">Phone: {llc.phone || ''}</div>}
          </div>
          
          {/* Right Column - Form ID */}
          <div className="w-1/2 p-2">
            <div className="flex justify-between items-start">
              <div className="text-xs">
                <div className="text-red-600 font-bold">OMB No. {config.omb}</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-red-600">{year}</div>
              </div>
            </div>
            <div className="mt-2 text-center">
              <div className="text-xl font-bold">{config.title}</div>
              <div className="text-xs text-gray-600">{config.subtitle}</div>
            </div>
          </div>
        </div>

        {/* TIN Section */}
        <div className="flex border-b-2 border-black">
          <div className="w-1/2 border-r-2 border-black p-2">
            <div className="text-[10px] text-red-600 font-bold">PAYER'S TIN</div>
            <div className="text-sm font-mono mt-1">{llc?.ein || 'XX-XXXXXXX'}</div>
          </div>
          <div className="w-1/2 p-2">
            <div className="text-[10px] text-red-600 font-bold">RECIPIENT'S TIN</div>
            <div className="text-sm font-mono mt-1">{payee?.tax_id || 'XXX-XX-XXXX'}</div>
          </div>
        </div>

        {/* Recipient Info */}
        <div className="flex border-b-2 border-black">
          <div className="w-1/2 border-r-2 border-black p-2">
            <div className="text-[10px] text-red-600 font-bold">RECIPIENT'S name</div>
            <div className="text-sm font-semibold mt-1">{payee?.name || 'Recipient Name'}</div>
          </div>
          <div className="w-1/2 p-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] text-red-600 font-bold">{config.box1Label}</div>
                <div className="text-lg font-mono font-bold mt-1">$ {formatCurrency(totalAmount)}</div>
              </div>
              <div>
                <div className="text-[10px] text-red-600 font-bold">{config.box2Label}</div>
                <div className="text-sm mt-1">{formType === '1099-NEC' ? '[ ]' : '$ 0.00'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Street Address */}
        <div className="flex border-b-2 border-black">
          <div className="w-1/2 border-r-2 border-black p-2">
            <div className="text-[10px] text-red-600 font-bold">Street address (including apt. no.)</div>
            <div className="text-sm mt-1">{payee?.address_line1 || ''}</div>
            {payee?.address_line2 && <div className="text-sm">{payee.address_line2}</div>}
          </div>
          <div className="w-1/2 p-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] text-red-600 font-bold">{formType === '1099-DIV' ? '2a Total capital gain distr.' : '3 Federal income tax withheld'}</div>
                <div className="text-sm font-mono mt-1">$ 0.00</div>
              </div>
              <div>
                <div className="text-[10px] text-red-600 font-bold">{formType === '1099-DIV' ? '2b Unrecap. Sec. 1250 gain' : '4 State tax withheld'}</div>
                <div className="text-sm font-mono mt-1">$ 0.00</div>
              </div>
            </div>
          </div>
        </div>

        {/* City, State, ZIP */}
        <div className="flex border-b-2 border-black">
          <div className="w-1/2 border-r-2 border-black p-2">
            <div className="text-[10px] text-red-600 font-bold">City or town, state or province, country, and ZIP or foreign postal code</div>
            <div className="text-sm mt-1">
              {[payee?.city, payee?.state, payee?.zip_code].filter(Boolean).join(', ')}
            </div>
          </div>
          <div className="w-1/2 p-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] text-red-600 font-bold">{formType === '1099-DIV' ? '3 Nondividend distributions' : '5 State/Payer\'s state no.'}</div>
                <div className="text-sm mt-1">{formType === '1099-DIV' ? '$ 0.00' : (llc?.state || '')}</div>
              </div>
              <div>
                <div className="text-[10px] text-red-600 font-bold">{formType === '1099-DIV' ? '4 Federal income tax withheld' : '6 State income'}</div>
                <div className="text-sm font-mono mt-1">{formType === '1099-DIV' ? '$ 0.00' : `$ ${formatCurrency(totalAmount)}`}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Account Number */}
        <div className="flex">
          <div className="w-1/2 border-r-2 border-black p-2">
            <div className="text-[10px] text-red-600 font-bold">Account number (see instructions)</div>
            <div className="text-sm font-mono mt-1"></div>
          </div>
          <div className="w-1/2 p-2">
            <div className="text-[10px] text-gray-600">
              Copy B - For Recipient
            </div>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-4 text-xs text-gray-600">
        <p className="font-bold">Instructions for Recipient</p>
        <p className="mt-1">
          This is important tax information and is being furnished to the IRS. If you are required to file a return, 
          a negligence penalty or other sanction may be imposed on you if this income is taxable and the IRS 
          determines that it has not been reported.
        </p>
        <p className="mt-2">
          <span className="font-bold">{config.instructions}</span>
        </p>
      </div>

      {/* Footer */}
      <div className="mt-8 pt-4 border-t text-center text-xs text-gray-500">
        <p>{config.title} (Rev. 1-{year})</p>
        <p>Department of the Treasury - Internal Revenue Service</p>
      </div>
    </div>
  );
});

Form1099PrintView.displayName = 'Form1099PrintView';
